import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, Card, CardContent, Checkbox, FormControlLabel, Button, Box,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import SmartSearch from '../components/SmartSearch';
import { quizManifest } from '../data/quizManifest';

const Homepage = () => {
    const [quizzes, setQuizzes] = useState({});
    const [selectedQuizzes, setSelectedQuizzes] = useState([]);
    // State to track which accordion panel is currently expanded
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const categorizedQuizzes = {};

        quizManifest.forEach(filename => {
            const parts = filename.replace('.json', '').split(' - ');
            const mainTopic = parts[0] || 'General';
            const subTopic = parts[1] || 'Quiz';

            if (!categorizedQuizzes[mainTopic]) {
                categorizedQuizzes[mainTopic] = [];
            }
            categorizedQuizzes[mainTopic].push({ subTopic, filename });
        });

        setQuizzes(categorizedQuizzes);
    }, []);

    const handleSelectQuiz = (filename) => {
        setSelectedQuizzes(prevSelected =>
            prevSelected.includes(filename)
                ? prevSelected.filter(f => f !== filename)
                : [...prevSelected, filename]
        );
    };

    const handleStartCustomQuiz = () => {
        if (selectedQuizzes.length > 0) {
            navigate('/quiz-setup', { state: { selectedFiles: selectedQuizzes } });
        }
    };

    // This function handles the accordion logic.
    // If the panel clicked is already open, it closes it (sets expanded to false).
    // Otherwise, it sets expanded to the new panel name.
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
                Create a Custom Quiz
            </Typography>
            <Typography align="center" color="text.secondary" sx={{ mb: 4 }}>
                Expand a topic below and select quizzes to begin.
            </Typography>

            <Box sx={{ mb: 4 }}>
                {Object.keys(quizzes).sort().map(mainTopic => (
                    <Accordion 
                        key={mainTopic} 
                        expanded={expanded === mainTopic} 
                        onChange={handleAccordionChange(mainTopic)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`${mainTopic}-content`}
                            id={`${mainTopic}-header`}
                        >
                            {/* Topic Name */}
                            <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold' }}>
                                {mainTopic}
                            </Typography>
                            {/* Quiz Count Helper Text */}
                            <Typography sx={{ color: 'text.secondary' }}>
                                {quizzes[mainTopic].length} {quizzes[mainTopic].length === 1 ? 'Quiz' : 'Quizzes'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor: 'background.default' }}>
                            <Grid container spacing={2}>
                                {quizzes[mainTopic].map(({ subTopic, filename }) => (
                                    <Grid item xs={12} sm={6} md={4} key={filename}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                                <FormControlLabel
                                                    sx={{ width: '100%', m: 0 }}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedQuizzes.includes(filename)}
                                                            onChange={() => handleSelectQuiz(filename)}
                                                        />
                                                    }
                                                    label={subTopic}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>

            <Box textAlign="center" my={4}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleStartCustomQuiz}
                    disabled={selectedQuizzes.length === 0}
                >
                    Configure & Start Quiz ({selectedQuizzes.length} selected)
                </Button>
            </Box>

            <SmartSearch />
        </Container>
    );
};

export default Homepage;
